import { Project } from './project.module';
import { Team } from './team.model';
import { User } from './user.model';

export interface PagedData<T> {
  count: number;
  next?: string;
  previous?: string;
  results: Array<T>;
}

// New API response structure
export interface ApiListResponse<T> {
  items: Array<T>;
  total: number;
  skip: number;
  limit: number;
}

export interface ReportPagedData<T> {
  page: number;
  pages: number;
  size: number;
  total: number;
  items: Array<T>;
}

export interface Area {
  geometry: Geometry;
  properties: AreaProperties;
  type?: string;
  // New API fields
  id?: string;
  name?: string;
  description?: string;
  project_id?: string;
  folder_id?: string;
  tenant_id?: string;
  team_id?: string;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
  geometry_type?: string;
  area_sq_meters?: number;
  perimeter_meters?: number;
  length_meters?: number;
  centroid?: {
    type: string;
    coordinates: number[];
  };
  bbox?: number[];
  area_metadata?: Record<string, unknown>;
  realm?: string;
  group?: string;
  project?: {
    id: string;
    name: string;
    path: string;
  };
  user?: {
    id: string;
    username: string;
    email: string;
    full_name: string;
  };
}

export type GeometryTypes =
  | 'Point'
  | 'LineString'
  | 'LinearRing'
  | 'Polygon'
  | 'MultiPoint'
  | 'MultiLineString'
  | 'MultiPolygon'
  | 'GeometryCollection'
  | 'Circle';

export interface Geometry {
  type: GeometryTypes;
  coordinates: number[] | number[][] | number[][][];
}

export interface AreaProperties {
  name: string;
  id: string;
  user?: User;
  project?: Project;
  team?: Partial<Team>;
  area_in_sqm?: number;
  area_sq_meters?: number;
  metadata?: JSON;
  properties?: JSON;
  groups?: Group[];
  group_ids?: number[];
  address?: Record<string, unknown>;
  from_distance?: number;
  to_distance?: number;
  created_at?: string;
  updated_at?: string;
  geometry_type?: string;
  status?: string;
  category?: string;
  priority?: string;
  verified?: boolean;
  year?: number;
  [key: string]: unknown;
}

export interface Group {
  id: string;
  name: string;
  user: Partial<User>;
  team: Partial<Team>;
  description: string;
  metadata?: JSON;
  feature_ids: number[];
}

export interface FeatureCollection<T> {
  type: string;
  features: Array<T>;
  meta?: {
    count: number;
    next: string;
    previous: string;
    current_window?: {
      end: number;
      start: number;
    };
  };
  total?: number;
}

export interface Cluster {
  clusters: Array<ClusterItem>;
  total_areas: number;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  zoom: number;
  precision: number;
  source: string;
  filter_support?: string;
}

export interface ClusterItem {
  cluster_id: string;
  count: number;
  centroid: {
    lat: number;
    lon: number;
  };
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  samples: Array<ClusterSample>;
}

export interface ClusterSample {
  id: string;
  name: string;
  location: {
    lat: number;
    lon: number;
  };
  properties: Record<string, any> | null;
}

export interface ClusterRequest {
  zoom: number;
  bounds: {
    west: number;
    south: number;
    east: number;
    north: number;
  };
  filters?: string;
}

export interface Centroid {
  centroid: {
    lat: number;
    lon: number;
  };
  count: number;
}

export interface MetadataFilter {
  key: string;
  value: string;
  type: string;
}

export interface FilterBody {
  sort: string;
  tags: Group[];
  metadata: MetadataFilter[];
}

export interface FileFilterBody {
  file_format: string | null;
  date: {
    from: Date | null;
    to: Date | null;
  };
  ownership: User[];
}

export interface ConfirmationDialogData {
  header: string;
  confirmText: string;
  cancelText?: string;
  body?: string;
}

export interface NotificationMsg {
  created_at: string;
  updated_at?: string;
  id: string;
  message: string;
  read: boolean;
  team: string;
  user: string;
  dismissed?: boolean;
  category?: NotificationCategoryEnum | string;
}

export interface DownloadMsg {
  fileName: string;
  total: number;
  loaded: number;
  percentage: number;
}

export enum NotificationCategoryEnum {
  USER_ADDED_TO_TEAM = 'user_added_to_team',
  USER_REMOVED_FROM_TEAM = 'user_removed_from_team',
  USER_ADDED_TO_PROJECT = 'user_added_to_project',
  USER_REMOVED_FROM_PROJECT = 'user_removed_from_project',
  USER_ROLE_UPDATED = 'user_role_updated',
  PROJECT_ARCHIVED = 'project_archived',
  PROJECT_DELETED = 'project_deleted',
}

// Legacy filter operators (deprecated - use new filter system)
export enum FilterOperatorsEnum {
  EQ = '__eq',
  GT = '__gt',
  GTE = '__gte',
  LT = '__lt',
  LTE = '__lte',
  CONTAINS = '__contains',
  IN = '__in',
  RANGE = '__range',
}

export const FilterOperatorLabels: Record<FilterOperatorsEnum, string> = {
  [FilterOperatorsEnum.EQ]: 'Equals',
  [FilterOperatorsEnum.GT]: 'Greater Than',
  [FilterOperatorsEnum.GTE]: 'Greater Than or Equal To',
  [FilterOperatorsEnum.LT]: 'Less Than',
  [FilterOperatorsEnum.LTE]: 'Less Than or Equal To',
  [FilterOperatorsEnum.CONTAINS]: 'Contains',
  [FilterOperatorsEnum.IN]: 'In List',
  [FilterOperatorsEnum.RANGE]: 'Range'
};

export const FilterOperatorSymbols: Record<FilterOperatorsEnum, string> = {
  [FilterOperatorsEnum.EQ]: '=',
  [FilterOperatorsEnum.GT]: '>',
  [FilterOperatorsEnum.GTE]: '≥',
  [FilterOperatorsEnum.LT]: '<',
  [FilterOperatorsEnum.LTE]: '≤',
  [FilterOperatorsEnum.CONTAINS]: 'Contains',
  [FilterOperatorsEnum.IN]: '',
  [FilterOperatorsEnum.RANGE]: 'Range'
};

export interface Download {
  state: 'PENDING' | 'IN_PROGRESS' | 'DONE';
  progress: number;
  total: number;
  loaded: number;
  content: Blob | null;
}

// Field Keys and Values API interfaces
export interface FieldKey {
  name: string;
  type: string;
}

export interface FieldKeysResponse {
  columns: FieldKey[];
  properties_keys: FieldKey[];
  area_metadata_keys: FieldKey[];
}

export interface FieldValuesResponse {
  field_name: string;
  field_type: string;
  values: any[];
  total: number;
  limit: number;
  offset: number;
  message: string | null;
}

export enum AggregationOperatorsEnum {
  SUM = 'SUM',
  AVG = 'AVG',
  MIN = 'MIN',
  MAX = 'MAX',
  COUNT = 'COUNT',
  MEDIAN = 'MEDIAN',
  STDDEV = 'STDDEV',
}

export const AggregationOperatorLabels: Record<
  AggregationOperatorsEnum,
  string
> = {
  [AggregationOperatorsEnum.SUM]: 'Sum',
  [AggregationOperatorsEnum.AVG]: 'Average',
  [AggregationOperatorsEnum.MIN]: 'Minimum',
  [AggregationOperatorsEnum.MAX]: 'Maximum',
  [AggregationOperatorsEnum.COUNT]: 'Count',
  [AggregationOperatorsEnum.MEDIAN]: 'Median',
  [AggregationOperatorsEnum.STDDEV]: 'Standard Deviation'
};

// New API search request structure
export interface NewApiSearchRequest {
  query: string;
  filters?: string;
  sorts?: string;
  skip?: number;
  limit?: number;
  cursor?: string;
  scopes?: string;
}

export interface ErrorStructure {
  loc: string[];
  msg: string;
  type: string;
}
